import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./WardPaywall.module.scss"

/**
 * WardPaywall (`/ward`) — an original, self-contained EXPIRATION / WIN-BACK
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it: the pitch is SECURITY
 * & PRIVACY — the ring of protections that guards a private vault.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Bring your ward back to whole." Your vault is guarded by a WARD — a faceted
 * protective crest whose six segments are the six real Premium protections that
 * watch over your library. While Premium rests, the ward's segments have
 * drifted apart and dimmed — the guard is off duty, though your photos stay
 * safe and sealed. The hero is that living crest: press and HOLD and the six
 * pieces are magnetically drawn back inward, converging and locking into a
 * single whole, glowing crest that latches "Warded." Each piece names its
 * protection as it locks home. The payoff is wholeness restored — not a photo
 * reveal, not a countdown.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the expiration cluster) ───
 * Deliberately far from its expiration neighbours (Standby control console,
 * Deep Freeze frost, Beacon harbour, Vigil nightwatch, Hallmark certificate,
 * Unbroken calendar, Keystone arch, Hearth fire):
 * Layout      : a RADIAL KINETIC EMBLEM on a fullscreen dark-sapphire canvas —
 *               one centred faceted crest, not a card list, a console, a
 *               calendar, a map or a receipt.
 * Interaction : PRESS-AND-HOLD-TO-CONVERGE — the six pieces fly inward in
 *               proportion to how long you hold, snapping whole on completion.
 *               New to the set: not a power switch (Standby), not a keypad
 *               (OnlyYou), not a dial (Tumbler), not a tilt-seal (Hallmark).
 * Metaphor    : a WARD / protective crest that is whole only when every guard
 *               is present — Gestalt wholeness, not a lock, a door or a flame.
 * Feature     : the six real protections ARE the crest's facets (each carries
 *               its real feature icon), not a separate bullet list.
 * Motion      : magnetic CONVERGENCE + snap-lock (pieces drawn to centre) — a
 *               deliberate turn away from the "light spreads across a row and
 *               features illuminate one by one" motion of the console/beacon set.
 * Value frame : WHOLENESS / continuity of protection — bring the guard back to
 *               complete; resume exactly where you left off.
 * Register    : calm, protective, quietly premium — cool sapphire guardianship,
 *               not warm-nostalgic (Hearth/Keepsake), not clinical (Standby),
 *               not icy-fear (Deep Freeze).
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every facet is a REAL Private Photo Vault protection (only-you lock · encrypted
 * Cloud Vault · Decoy Vault · break-in snapshots · recently-deleted recovery ·
 * app disguise); the copy names them and claims nothing else. It states plainly
 * that your photos stay safe and sealed and that only the premium guard is
 * paused — no data-loss-on-expiration threat, no countdown, no fabricated
 * scarcity. "Save 79%" is real arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small & secondary. The
 * 7-day free trial is the real trial; billing terms read from real products.
 */

// The six real, security-first Premium protections — one per crest facet.
// Geometry precomputed for a pointy-top hexagon crest (200×200 viewBox, centre
// 100,100). `disp` is the dispersed (guard-resting) outward offset in user
// units; `icon` is where the protection's glyph sits inside its facet.
const FACETS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know",
    points: "100,100 100,22 167.55,61",
    icon: [122, 61.89],
    disp: [32, -55.43],
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup — for your eyes only",
    points: "100,100 167.55,61 167.55,139",
    icon: [144, 100],
    disp: [64, 0],
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
    points: "100,100 167.55,139 100,178",
    icon: [122, 138.11],
    disp: [32, 55.43],
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless, empty vault",
    points: "100,100 100,178 32.45,139",
    icon: [78, 138.11],
    disp: [-32, 55.43],
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Recently-deleted recovery",
    line: "bring a deleted photo back from the vault's Trash",
    points: "100,100 32.45,139 32.45,61",
    icon: [56, 100],
    disp: [-64, 0],
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the vault behind an ordinary-looking app",
    points: "100,100 32.45,61 100,22",
    icon: [78, 61.89],
    disp: [-32, -55.43],
  },
]

// how far into the hold each facet locks (staggered snap, outer→in feel)
const LOCK_AT = [0.55, 0.62, 0.69, 0.76, 0.83, 0.9]
const HOLD_MS = 1150 // full press-and-hold duration to re-form the ward

const WardPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the ward: progress 0 (dispersed) → 1 (whole & warded) ─────────────────
  const [progress, setProgress] = useState(0)
  const [warded, setWarded] = useState(false)
  const holding = useRef(false)
  const raf = useRef<number | null>(null)
  const startRef = useRef(0)
  const baseRef = useRef(0) // progress at the moment the current animation began
  const userActed = useRef(false)

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  // drive progress toward a target (1 = converge, 0 = drift back apart)
  const animateTo = useCallback(
    (target: 1 | 0, durationMs: number) => {
      stopRaf()
      baseRef.current = progressRef.current
      startRef.current = performance.now()
      const from = baseRef.current
      const step = (now: number) => {
        const t = Math.min(1, (now - startRef.current) / durationMs)
        const eased = target === 1 ? t * t * (3 - 2 * t) : t // smootherstep in
        const value = from + (target - from) * eased
        setProgressValue(value)
        if (t < 1) {
          raf.current = requestAnimationFrame(step)
        } else {
          raf.current = null
          if (target === 1) setWarded(true)
        }
      }
      raf.current = requestAnimationFrame(step)
    },
    [stopRaf],
  )

  // keep a ref mirror of progress for the rAF closure
  const progressRef = useRef(0)
  const setProgressValue = useCallback((v: number) => {
    progressRef.current = v
    setProgress(v)
  }, [])

  // hold-to-converge: while pressed, march progress up over HOLD_MS
  const beginHold = useCallback(() => {
    userActed.current = true
    if (warded) {
      // already whole — a press replays it: scatter, then let them re-form
      setWarded(false)
      stopRaf()
      setProgressValue(0)
    }
    holding.current = true
    stopRaf()
    baseRef.current = progressRef.current
    startRef.current = performance.now()
    const remaining = (1 - baseRef.current) * HOLD_MS
    const step = (now: number) => {
      if (!holding.current) return
      const t = Math.min(1, (now - startRef.current) / Math.max(1, remaining))
      const value = baseRef.current + (1 - baseRef.current) * t
      setProgressValue(value)
      if (value >= 1) {
        holding.current = false
        raf.current = null
        setWarded(true)
        return
      }
      raf.current = requestAnimationFrame(step)
    }
    raf.current = requestAnimationFrame(step)
  }, [warded, stopRaf, setProgressValue])

  const endHold = useCallback(() => {
    if (!holding.current) return
    holding.current = false
    if (progressRef.current < 1) {
      // released early — the pieces gently drift back apart
      animateTo(0, 620)
    }
  }, [animateTo])

  // keyboard / quick tap: run the whole convergence for accessibility
  const quickReform = useCallback(() => {
    userActed.current = true
    if (warded) {
      setWarded(false)
      stopRaf()
      setProgressValue(0)
      // brief scatter, then re-form so the mechanic stays legible
      window.setTimeout(() => animateTo(1, HOLD_MS), 240)
      return
    }
    animateTo(1, HOLD_MS * (1 - progressRef.current) + 120)
  }, [warded, stopRaf, setProgressValue, animateTo])

  // auto-demo on mount: re-form the ward so the payoff reads at a glance
  useEffect(() => {
    if (reduceMotion) {
      setProgressValue(1)
      setWarded(true)
      return
    }
    const t = window.setTimeout(() => {
      if (!userActed.current) animateTo(1, HOLD_MS + 250)
    }, 620)
    return () => {
      window.clearTimeout(t)
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // which facets have locked home (for label + glow), by progress
  const lockedCount = useMemo(
    () => LOCK_AT.filter((l) => progress >= l).length,
    [progress],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const p = progress
  const glow = warded ? 1 : Math.max(0, (p - 0.45) / 0.55)

  return (
    <div
      className={`${styles.root} ${warded ? styles.isWarded : ""}`}
      style={{ ["--p" as any]: p, ["--glow" as any]: glow }}
    >
      <div className={styles.aura} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          Your guard is resting
        </span>
        <h1 className={styles.title}>
          Bring your ward
          <br />
          <span className={styles.titleAccent}>back to whole.</span>
        </h1>
        <p className={styles.sub}>
          Your photos are safe and sealed. It&rsquo;s the six guards around them
          that have gone quiet. Draw them back together and pick up right where
          you left off.
        </p>
      </section>

      {/* ── the ward crest ─────────────────────────────────────────────────── */}
      <section className={styles.stage}>
        <div
          className={styles.crestWrap}
          role="button"
          tabIndex={0}
          aria-label="Press and hold to re-form your ward"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture?.(e.pointerId)
            beginHold()
          }}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          onPointerCancel={endHold}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              quickReform()
            }
          }}
        >
          <svg
            className={styles.crest}
            viewBox="0 0 200 200"
            aria-hidden="true"
          >
            <defs>
              <radialGradient id="wardCore" cx="50%" cy="42%" r="65%">
                <stop offset="0%" stopColor="#bcd0ff" />
                <stop offset="55%" stopColor="#3d6bf6" />
                <stop offset="100%" stopColor="#1b3aa8" />
              </radialGradient>
              <linearGradient id="wardFacet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f68e8" />
                <stop offset="100%" stopColor="#22307e" />
              </linearGradient>
              <filter
                id="wardGlow"
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feGaussianBlur stdDeviation="3.4" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* faint seat the whole crest snaps into */}
            <polygon
              className={styles.seat}
              points="100,22 167.55,61 167.55,139 100,178 32.45,139 32.45,61"
            />

            {/* the six protection facets */}
            {FACETS.map((f, i) => {
              const locked = p >= LOCK_AT[i]
              const dx = f.disp[0] * (1 - p)
              const dy = f.disp[1] * (1 - p)
              const rot = (f.disp[0] >= 0 ? 26 : -26) * (1 - p)
              const op = 0.26 + 0.74 * Math.min(1, p / LOCK_AT[i])
              return (
                <g
                  key={f.key}
                  className={`${styles.facet} ${locked ? styles.facetLocked : ""}`}
                  style={{
                    transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)`,
                    transformOrigin: "100px 100px",
                    transformBox: "view-box",
                    opacity: op,
                  }}
                >
                  <polygon
                    className={styles.facetShape}
                    points={f.points}
                    filter={locked ? "url(#wardGlow)" : undefined}
                  />
                  <line
                    className={styles.facetEdge}
                    x1="100"
                    y1="100"
                    x2={f.points.split(" ")[1].split(",")[0]}
                    y2={f.points.split(" ")[1].split(",")[1]}
                  />
                  <g
                    className={styles.facetIcon}
                    style={{
                      transform: `translate(${f.icon[0] - 12}px, ${
                        f.icon[1] - 12
                      }px)`,
                      transformBox: "view-box",
                    }}
                  >
                    <f.Icon width={24} height={24} />
                  </g>
                </g>
              )
            })}

            {/* the core — the vault it all guards */}
            <g className={styles.core}>
              <circle
                cx="100"
                cy="100"
                r="21"
                fill="url(#wardCore)"
                filter="url(#wardGlow)"
              />
              <path
                className={styles.coreCheck}
                d="M91 100.5l6 6 12.5-13"
                fill="none"
                stroke="#eaf1ff"
                strokeWidth="3.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          </svg>

          {/* hold ring progress + hint */}
          <svg
            className={styles.holdRing}
            viewBox="0 0 200 200"
            aria-hidden="true"
          >
            <circle className={styles.holdTrack} cx="100" cy="100" r="94" />
            <circle
              className={styles.holdFill}
              cx="100"
              cy="100"
              r="94"
              style={{ ["--dash" as any]: 1 - p }}
            />
          </svg>
        </div>

        <div className={styles.readout} aria-live="polite">
          <span className={styles.readState}>
            {warded ? "Warded — every guard back on watch" : "Ward at rest"}
          </span>
          <span className={styles.readCount}>
            <b>{lockedCount}</b>
            <span>/ 6 guards</span>
          </span>
        </div>

        <p className={styles.hint}>
          {warded
            ? "Hold again to replay"
            : "Press & hold the crest to draw your guards home"}
        </p>
      </section>

      {/* the six protections, named — a compact legend of the facets above */}
      <ul className={styles.legend}>
        {FACETS.map((f, i) => {
          const locked = lockedCount > i || warded
          return (
            <li
              key={f.key}
              className={`${styles.leg} ${locked ? styles.legOn : ""}`}
            >
              <span className={styles.legIcon} aria-hidden="true">
                <f.Icon width={17} height={17} />
              </span>
              <span className={styles.legText}>
                <span className={styles.legName}>{f.name}</span>
                <span className={styles.legLine}>{f.line}</span>
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Every facet is a real Private Photo Vault protection. Your library stays
        safe and sealed on your device the whole time — only these premium
        guards are paused. Re-forming your ward turns them back on and resumes
        from today; nothing is ever deleted.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ───────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · full guard</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Re-form my ward — start free"
              : "Re-form my ward — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · resumes today · cancel anytime`
              : `${monthlyPrice}/mo · resumes today · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default WardPaywall
